import { React, Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import AccordionDrawer from "./AccordionDrawer";
import DocumentLink from "./DocumentLink";
import ImageDownload from "components/utility/images/ImageDownload";
import ImageLinkOut from "components/utility/images/ImageLinkOut";
import { getDecodedHtml } from "utils/getDecodedHtml";
import DotLoader from "components/utility/dot-loader/DotLoader";

const DocumentCenterList = ({ isLoading, isError, errorMessage, data }) => {
	const [allExpanded, setAllExpanded] = useState(true);
	const { documentGridData, additionalDocuments, generalDocuments } = data;
	const { linkDrawers = [], toggleCollapseText = { collapse: "", expand: "" } } = documentGridData;

	const decodedAdditonalDocuments = {
		title: getDecodedHtml(additionalDocuments?.title),
		links: additionalDocuments?.links.map((link) => {
			return { ...link, name: getDecodedHtml(link.name) };
		})
	};

	const allDrawers =
		additionalDocuments && linkDrawers
			? linkDrawers.concat(decodedAdditonalDocuments)
			: linkDrawers;

	const expandCollapseText =
		allExpanded === false ? toggleCollapseText.expand : toggleCollapseText.collapse;

	const listItemIcon = (type) => {
		if (type === "download") {
			return <ImageDownload />;
		}
		if (type === "external") {
			return <ImageLinkOut />;
		}
		return null; // no icon for internal links
	};

	// hide general documents drawer if data is loading or error
	const generalDocumentsDrawer = document.querySelector("#general-documents-drawer-container");
	useEffect(() => {
		if (generalDocumentsDrawer) {
			if (isLoading || isError) {
				generalDocumentsDrawer.style.display = "none";
			} else if (!isLoading || !isError) {
				generalDocumentsDrawer.style.display = "block";
			}
			return;
		}
	}, [generalDocumentsDrawer, isLoading, isError]);

	return (
		<Fragment>
			{!isLoading && !isError ? (
				<div className="list-controls container">
					<div className="grid">
						<div>
							<button
								className="toggle-collapse"
								onClick={() => setAllExpanded(!allExpanded)}
								tabIndex="0"
								dangerouslySetInnerHTML={{ __html: getDecodedHtml(expandCollapseText) }}
							></button>
						</div>
					</div>
				</div>
			) : null}
			{!isLoading && !isError ? (
				<div className="drawer-list-container container">
					<div className="grid">
						<div className="drawer-list">
							{allDrawers.map((item, index) => {
								const rowRepeatCount = Math.ceil(item.links.length / 2);
								return (
									<AccordionDrawer
										key={index}
										uid={index}
										data={item}
										expandOverrideStatus={allExpanded}
									>
										<ul
											style={{
												gridTemplateRows: `repeat(${rowRepeatCount}, minmax(min-content, max-content))`
											}}
										>
											{item.links.map((item, index) => {
												const icon = listItemIcon(item.type);
												return <DocumentLink key={index} item={item} icon={icon} />;
											})}
										</ul>
									</AccordionDrawer>
								);
							})}
						</div>
						<div className="general-documents-ribbon-column">
							{generalDocuments ? (
								<div className="general-documents-ribbon">
									<Fragment>
										<div
											className="general-documents-ribbon-title"
											dangerouslySetInnerHTML={{
												__html: getDecodedHtml(generalDocuments.title)
											}}
										></div>
										<ul>
											{generalDocuments.links.map((item, index) => {
												const icon = listItemIcon(item.type);
												return <DocumentLink key={index} item={item} icon={icon} />;
											})}
										</ul>
									</Fragment>
									<div className="general-documents-ribbon-overhang"></div>
								</div>
							) : null}
						</div>
					</div>
				</div>
			) : !isError ? (
				<div className="dot-loader-container container">
					<div className="grid">
						<DotLoader />
					</div>
				</div>
			) : (
				<div className="error-message-container container">
					<div className="grid">
						<div className="error-message">{errorMessage}</div>
					</div>
				</div>
			)}
		</Fragment>
	);
};

DocumentCenterList.propTypes = {
	data: PropTypes.shape({
		documentGridData: PropTypes.shape({
			linkDrawers: PropTypes.arrayOf(
				PropTypes.shape({
					title: PropTypes.string,
					subtitle: PropTypes.string,
					links: PropTypes.arrayOf(
						PropTypes.shape({
							name: PropTypes.string,
							url: PropTypes.string,
							type: PropTypes.string
						})
					)
				})
			),
			toggleCollapseText: PropTypes.shape({
				collapse: PropTypes.string,
				expand: PropTypes.string
			})
		}),
		additionalDocuments: PropTypes.shape({
			title: PropTypes.string,
			links: PropTypes.array
		}),
		generalDocuments: PropTypes.shape({
			title: PropTypes.string,
			links: PropTypes.array
		})
	}).isRequired
};

DocumentCenterList.defaultProps = {
	data: {
		documentGridData: {
			linkDrawers: [
				{
					title: "",
					subtitle: "",
					links: [
						{
							name: "",
							url: "",
							type: ""
						}
					]
				}
			],
			toggleCollapseText: {
				collapse: "",
				expand: ""
			}
		},
		additionalDocuments: {
			title: "",
			links: []
		},
		generalDocuments: {
			title: "",
			links: []
		}
	}
};

export default DocumentCenterList;
